import React from 'react';
import PropTypes from 'prop-types';
import TagManager from 'react-gtm-module';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import styles from './styles';
import JobCard from '../../../JobCard';
import { sortObjectByKey, constructGTM } from '../../../../utilities/helpers';
import { SORT_DIRECTIONS } from '../../../../constants';

const propTypes = {
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      rawId: PropTypes.number.isRequired,
      id: PropTypes.string.isRequired,
      dateCreated: PropTypes.string.isRequired,
      customerDetail: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string,
      }),
      status: PropTypes.string.isRequired,
      notes: PropTypes.string,
    }),
  ),
  classes: PropTypes.shape({
    jobsInfoWrapper: PropTypes.string,
    jobsList: PropTypes.string,
    jobItem: PropTypes.string,
    createJobButton: PropTypes.string,
  }).isRequired,
  sort: PropTypes.shape({
    column: PropTypes.string,
    direction: PropTypes.oneOf([SORT_DIRECTIONS.ASC, SORT_DIRECTIONS.DESC]),
  }).isRequired,
  getJobActions: PropTypes.func.isRequired,
  openCreateModal: PropTypes.func.isRequired,
  handleJobStatusChange: PropTypes.func.isRequired,
};

const defaultProps = {
  jobs: [],
};

const columns = [
  {
    index: 0,
    title: 'ID',
    name: 'id',
    sort: {
      enabled: false,
    },
  },
  {
    index: 1,
    title: 'Date Created',
    name: 'dateCreated',
    sort: {
      enabled: false,
    },
  },
  {
    index: 2,
    title: 'Customer Name',
    name: 'customerDetail_name',
    type: 'date',
    sort: {
      enabled: false,
    },
  },
  {
    index: 3,
    title: 'Email',
    name: 'customerDetail_email',
    sort: {
      enabled: false,
    },
  },
  {
    index: 4,
    title: 'Phone',
    name: 'customerDetail_phone',
    sort: {
      enabled: false,
    },
  },
  {
    index: 5,
    title: 'Lead Status',
    name: 'status',
    sort: {
      enabled: false,
    },
  },
  {
    index: 6,
    title: '',
    name: 'actions',
    sort: {
      enabled: false,
    },
  },
];

export class JobsContainer extends React.PureComponent {
  getSortedJobsList = (jobs, sort) => {
    const column = columns.find(col => col.name === sort.column);

    // this column does exist
    if (column !== undefined) {
      const rows = jobs.map(order => ({ ...order }));

      // this column has a custom sort function
      if (column.customSort) {
        return rows.sort(column.customSort(sort.column, sort.direction));
      }

      return rows.sort(sortObjectByKey(sort.column, sort.direction));
    }

    // the column does not exist, so there must be something wrong with the data
    // then we return an empty array
    return [];
  };

  render() {
    const {
      classes,
      jobs,
      sort,
      getJobActions,
      openCreateModal,
      handleJobStatusChange,
    } = this.props;

    const registerEventToGtm = () => {
      // Log event to GTM
      TagManager.initialize(
        constructGTM({
          dataLayer: {
            event: 'User clicked on Create New Job',
          },
        }),
      );
    };

    const tooltipText =
      'You can now keep track of all of your jobs in one place. Click here to add a new job to your dashboard. Please note, you are not required to create a new job when using our Take-off tools. If you need assistance, please phone 13 11 03.';

    return (
      <div>
        <div className={classes.jobsInfoWrapper}>
          <div className={classes.jobsList}>
            {this.getSortedJobsList(jobs, sort).map((job, index) => (
              <div
                className={classes.jobItem}
                key={job.id}
                data-test="job-card-wrapper-mobile"
              >
                <JobCard
                  index={index}
                  job={job}
                  actions={getJobActions(job)}
                  handleJobStatusChange={handleJobStatusChange}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={classes.createJobContainer}>
          <div className={classes.createJobTooltip}>
            <Tooltip title={tooltipText} placement="top" disableTouchListener>
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Button
            variant="contained"
            className={classes.createJobButton}
            color="primary"
            onClick={() => {
              registerEventToGtm();
              openCreateModal();
            }}
          >
            Create new job
          </Button>
        </div>
      </div>
    );
  }
}

JobsContainer.propTypes = propTypes;
JobsContainer.defaultProps = defaultProps;

export default withStyles(styles)(JobsContainer);
